/**
 * @module Actions.CalendarPreferences
 *
 */
import { Dispatch } from "react";
import { ActionType, action } from "typesafe-actions";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import {
  CalendarPreferences,
  DEFAULT_CALENDAR_PREFERENCES,
} from "app/models/CalendarPreferences";
import { RootState } from "typedefs";

type BookingPolicySettings = {
  calendarFirstDayOfWeek?: number;
  calendarTimeFormat?: "12h" | "24h";
  calendarDateFormat?: "us" | "eu";
};

type BookingPolicyResponse = {
  settings?: BookingPolicySettings;
};

const pending = () => action("@calendarPreferences.fetch.pending");
const error = (err: string) => action("@calendarPreferences.fetch.error", err);
const success = (data: CalendarPreferences) =>
  action("@calendarPreferences.fetch.success", data);
const localUpdate = (partial: Partial<CalendarPreferences>) =>
  action("@calendarPreferences.update", partial);

const thisActions = { pending, error, success, localUpdate };

export type CalendarPreferencesAction = ActionType<typeof thisActions>;

export const updatePreferences = localUpdate;

export function fetchCalendarPreferences(force = false) {
  return async (
    dispatch: Dispatch<CalendarPreferencesAction>,
    getState: () => RootState,
  ) => {
    const state = getState();
    const coachId = state.user.self?.id;
    if (!coachId) {
      return;
    }

    const slice = state.calendarPreferences;
    if (slice.pending) {
      return;
    }
    if (!force && slice.data !== null) {
      return;
    }

    dispatch(pending());
    try {
      const response = await schedulingFetchGet<BookingPolicyResponse>(
        `coach_profiles/${coachId}/booking_policy`,
      );
      const settings = (response as BookingPolicyResponse)?.settings;
      const merged: CalendarPreferences = {
        ...(slice.data ?? DEFAULT_CALENDAR_PREFERENCES),
        firstDay:
          settings?.calendarFirstDayOfWeek ??
          slice.data?.firstDay ??
          DEFAULT_CALENDAR_PREFERENCES.firstDay,
        timeFormat:
          settings?.calendarTimeFormat ??
          slice.data?.timeFormat ??
          DEFAULT_CALENDAR_PREFERENCES.timeFormat,
        dateFormat:
          settings?.calendarDateFormat ??
          slice.data?.dateFormat ??
          DEFAULT_CALENDAR_PREFERENCES.dateFormat,
      };
      dispatch(success(merged));
    } catch (err) {
      dispatch(error(String(err)));
    }
  };
}
